import * as THREE from "three";
//#region ../src/ar/sun-marker.ts
/**
* The AR sun marker (plan 2026-09-24-0100, M2, §3.3): a virtual sun drawn
* over the real one, with a heading scale, so the alignment's heading error
* can be read by eye.
*
* DIRECTION-ONLY, LIKE THE SKY. Every vertex is a DIRECTION, placed with the
* rotation-only view (`projectionMatrix * vec4(mat3(viewMatrix) * d, 1)`),
* evaluated with the matrices three sets at render time: no lag behind the
* camera, no parallax, correct for each XR view, and never clipped by the
* far plane (plan §3.2). The mesh sits on the scene ROOT, which is the
* GPS-world NUE frame.
*
* GEOMETRY IN ANGLES. Each vertex carries `sunOffset` = (Δazimuth, u, v) in
* degrees; the vertex shader builds the direction exactly as
* `markerVertexDirection` in `sun-check-geometry.ts` does (its JS twin, which
* the tests use). Heading ticks are anchored on the almucantar at Δazimuth =
* N; everything else hangs off the sun itself. Rings are built at their TRUE
* angular radius (the gnomonic plane's radius is tan r).
*
* OVER EVERYTHING, UNGRADED. No depth test or write, no fog, no tone mapping
* (so OsmDemo's Khronos Neutral grade cannot shift the magenta), drawn last,
* never frustum-culled. A dark outline under each line keeps it visible on a
* white, blown-out sky.
*
* Tick LABELS are not drawn yet (a text atlas, plan §3.3); the rings sit at
* fixed, documented radii so the scale reads without them.
*
* @see sun-marker.ts.md
*/
/** The marker's layout, degrees and colours, in one place. */
const SUN_MARKER = {
	/** The disc (the sun's real diameter is 0.53°) and the distance rings. */
	ringsDeg: [
		.265,
		1,
		2,
		5
	],
	ringSegments: 96,
	/** Crosshair arms reach this far, with a gap around the centre. */
	armLengthDeg: 6,
	armGapDeg: .6,
	/** Heading ticks: Δazimuth on the almucantar, in heading degrees. */
	headingTicksDeg: [
		-5,
		-2,
		-1,
		1,
		2,
		5
	],
	headingTickHalfLengthDeg: .35,
	/** Elevation ticks on the vertical arm. */
	elevationTicksDeg: [
		-1.5,
		-.5,
		.5,
		1.5
	],
	elevationTickHalfLengthDeg: .25,
	/** The centre dot's half size. */
	dotHalfDeg: .04,
	/** Line widths: about 2 px and 4 px on a phone's ~70° view. */
	lineWidthDeg: .1,
	outlineWidthDeg: .25,
	colour: 16711935,
	outlineColour: 1703968,
	/** Drawn after everything else. */
	renderOrder: 1e6,
	/**
	* The screen-centre reticle, in NDC x units (y scaled by the aspect so it
	* is square): the arms run from the gap to the reach, so the real sun at
	* the centre stays visible. It marks NDC (0, 0), the pixel
	* `principalRayCamera` unprojects for a Mark (plan §3.4).
	*/
	reticleGapNdc: .012,
	reticleReachNdc: .05,
	reticleWidthNdc: .004,
	reticleOutlineNdc: .009,
	reticleColour: 16777215
};
const DEG = Math.PI / 180;
const VERTEX_SHADER = `
uniform float sunAzimuthDeg;
uniform float sunElevationDeg;
attribute vec3 sunOffset;
attribute vec3 markerColor;
varying vec3 vMarkerColor;
const float DEG = 0.017453292519943295;
void main() {
  // The twin of markerVertexDirection (sun-check-geometry.ts).
  float az = (sunAzimuthDeg + sunOffset.x) * DEG;
  float el = sunElevationDeg * DEG;
  vec3 anchor = vec3(cos(el) * cos(az), sin(el), cos(el) * sin(az));
  vec3 east = vec3(-sin(az), 0.0, cos(az));
  vec3 up = cross(east, anchor);
  vec3 d = normalize(anchor + tan(sunOffset.y * DEG) * east + tan(sunOffset.z * DEG) * up);
  vMarkerColor = markerColor;
  gl_Position = projectionMatrix * vec4(mat3(viewMatrix) * d, 1.0);
  // Depth 0 (mid-range) so neither the near nor the far plane can clip a
  // direction in front of the camera; depth is not tested. A direction
  // behind the camera has w < 0 and stays clipped.
  gl_Position.z = 0.0;
}
`;
const FRAGMENT_SHADER = `
varying vec3 vMarkerColor;
void main() {
  gl_FragColor = vec4(vMarkerColor, 1.0);
}
`;
const RETICLE_VERTEX_SHADER = `
attribute vec3 markerColor;
varying vec3 vMarkerColor;
void main() {
  // Clip space, fixed on the screen: NDC (position.x, position.y * aspect),
  // the aspect read from the projection so the cross stays square.
  float aspect = projectionMatrix[1][1] / projectionMatrix[0][0];
  vMarkerColor = markerColor;
  gl_Position = vec4(position.x, position.y * aspect, 0.0, 1.0);
}
`;
/** The screen-centre crosshair: four arms, outline then fill, in NDC. */
function buildReticleGeometry() {
	const m = SUN_MARKER;
	const arms = (half) => {
		const quads = [];
		const push = (x0, y0, x1, y1) => quads.push(x0, y0, 0, x1, y0, 0, x1, y1, 0, x0, y0, 0, x1, y1, 0, x0, y1, 0);
		push(m.reticleGapNdc, -half, m.reticleReachNdc, half);
		push(-m.reticleReachNdc, -half, -m.reticleGapNdc, half);
		push(-half, m.reticleGapNdc, half, m.reticleReachNdc);
		push(-half, -m.reticleReachNdc, half, -m.reticleGapNdc);
		return quads;
	};
	const outline = arms(m.reticleOutlineNdc / 2);
	const fill = arms(m.reticleWidthNdc / 2);
	const positions = new Float32Array([...outline, ...fill]);
	const colours = new Float32Array(positions.length);
	const dark = new THREE.Color(m.outlineColour);
	const light = new THREE.Color(m.reticleColour);
	for (let i = 0; i < positions.length / 3; i++) {
		const c = i < outline.length / 3 ? dark : light;
		colours.set([
			c.r,
			c.g,
			c.b
		], i * 3);
	}
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
	geometry.setAttribute("markerColor", new THREE.BufferAttribute(colours, 3));
	return geometry;
}
/** The gnomonic (u, v) of a TRUE angular radius r at polar angle φ. */
function ringPoint(rDeg, phi) {
	const t = Math.tan(rDeg * DEG);
	return [Math.atan(t * Math.cos(phi)) / DEG, Math.atan(t * Math.sin(phi)) / DEG];
}
/** Two triangles for a quad a-b-c-d (in order around it). */
function quad(out, a, b, c, d) {
	out.push(a, b, c, a, c, d);
}
/** A straight line between two (u, v) points at one anchor, as a quad. */
function line(out, dAz, from, to, width) {
	const du = to[0] - from[0];
	const dv = to[1] - from[1];
	const length = Math.hypot(du, dv);
	const nu = -dv / length * (width / 2);
	const nv = du / length * (width / 2);
	quad(out, [
		dAz,
		from[0] + nu,
		from[1] + nv
	], [
		dAz,
		to[0] + nu,
		to[1] + nv
	], [
		dAz,
		to[0] - nu,
		to[1] - nv
	], [
		dAz,
		from[0] - nu,
		from[1] - nv
	]);
}
/** A ring at true radius r, as quads between r ± width / 2. */
function ring(out, rDeg, width) {
	const n = SUN_MARKER.ringSegments;
	const inner = Math.max(0, rDeg - width / 2);
	const outer = rDeg + width / 2;
	for (let i = 0; i < n; i++) {
		const a = 2 * Math.PI * i / n;
		const b = 2 * Math.PI * (i + 1) / n;
		const [ia, oa, ib, ob] = [
			ringPoint(inner, a),
			ringPoint(outer, a),
			ringPoint(inner, b),
			ringPoint(outer, b)
		];
		quad(out, [
			0,
			ia[0],
			ia[1]
		], [
			0,
			oa[0],
			oa[1]
		], [
			0,
			ob[0],
			ob[1]
		], [
			0,
			ib[0],
			ib[1]
		]);
	}
}
/** Every shape of the marker, at one line width. */
function shapes(width) {
	const out = [];
	const m = SUN_MARKER;
	for (const r of m.ringsDeg) ring(out, r, width);
	for (const sign of [-1, 1]) {
		line(out, 0, [sign * m.armGapDeg, 0], [sign * m.armLengthDeg, 0], width);
		line(out, 0, [0, sign * m.armGapDeg], [0, sign * m.armLengthDeg], width);
	}
	for (const n of m.headingTicksDeg) {
		const h = m.headingTickHalfLengthDeg;
		line(out, n, [0, -h], [0, h], width);
	}
	for (const v of m.elevationTicksDeg) {
		const h = m.elevationTickHalfLengthDeg;
		line(out, 0, [-h, v], [h, v], width);
	}
	const s = m.dotHalfDeg + (width - m.lineWidthDeg) / 2;
	quad(out, [
		0,
		-s,
		-s
	], [
		0,
		s,
		-s
	], [
		0,
		s,
		s
	], [
		0,
		-s,
		s
	]);
	return out;
}
function buildGeometry() {
	const outline = shapes(SUN_MARKER.outlineWidthDeg);
	const fill = shapes(SUN_MARKER.lineWidthDeg);
	const all = [...outline, ...fill];
	const offsets = new Float32Array(all.length * 3);
	const colours = new Float32Array(all.length * 3);
	const dark = new THREE.Color(SUN_MARKER.outlineColour);
	const bright = new THREE.Color(SUN_MARKER.colour);
	all.forEach((p, i) => {
		offsets.set(p, i * 3);
		const c = i < outline.length ? dark : bright;
		colours.set([
			c.r,
			c.g,
			c.b
		], i * 3);
	});
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(all.length * 3), 3));
	geometry.setAttribute("sunOffset", new THREE.BufferAttribute(offsets, 3));
	geometry.setAttribute("markerColor", new THREE.BufferAttribute(colours, 3));
	return geometry;
}
/** Builds the marker. Owns no clock and no store. */
function createSunMarker() {
	const material = new THREE.ShaderMaterial({
		name: "sun-marker",
		vertexShader: VERTEX_SHADER,
		fragmentShader: FRAGMENT_SHADER,
		uniforms: {
			sunAzimuthDeg: { value: 0 },
			sunElevationDeg: { value: 0 }
		},
		depthTest: false,
		depthWrite: false,
		transparent: true,
		toneMapped: false,
		fog: false,
		side: THREE.DoubleSide,
		forceSinglePass: true
	});
	const object = new THREE.Mesh(buildGeometry(), material);
	object.name = "sun-marker";
	object.frustumCulled = false;
	object.renderOrder = SUN_MARKER.renderOrder;
	const reticleMaterial = new THREE.ShaderMaterial({
		name: "sun-reticle",
		vertexShader: RETICLE_VERTEX_SHADER,
		fragmentShader: FRAGMENT_SHADER,
		depthTest: false,
		depthWrite: false,
		transparent: true,
		toneMapped: false,
		fog: false,
		side: THREE.DoubleSide,
		forceSinglePass: true
	});
	const reticle = new THREE.Mesh(buildReticleGeometry(), reticleMaterial);
	reticle.name = "sun-reticle";
	reticle.frustumCulled = false;
	reticle.renderOrder = SUN_MARKER.renderOrder + 1;
	return {
		object,
		reticle,
		setSun(azimuthDeg, elevationDeg) {
			if (!Number.isFinite(azimuthDeg) || !Number.isFinite(elevationDeg)) throw new RangeError("sun angles must be finite");
			if (Math.abs(elevationDeg) > 90) throw new RangeError(`elevation must be within ±90°, got ${elevationDeg}`);
			material.uniforms.sunAzimuthDeg.value = azimuthDeg;
			material.uniforms.sunElevationDeg.value = elevationDeg;
		},
		setVisible(visible) {
			object.visible = visible;
			reticle.visible = visible;
		},
		dispose() {
			object.removeFromParent();
			object.geometry.dispose();
			material.dispose();
			reticle.removeFromParent();
			reticle.geometry.dispose();
			reticleMaterial.dispose();
		}
	};
}
//#endregion
export { SUN_MARKER, createSunMarker };
